import Box from '@mui/material/Box';
import MenuItem from '@mui/material/MenuItem';
import TextField from '@mui/material/TextField';
import { useMemo, type ChangeEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { trackDatadogAction } from '@proposify/frontend-observability';
import type { PageOrientation } from '../../shared/gridConfig';
import { pageOrientationSelectTestIds } from './PageOrientationSelect.test-ids';

export type OrientationOption = {
  value: PageOrientation;
  label: string;
};

interface PageOrientationSelectProps {
  value?: PageOrientation;
  onChange?: (value: PageOrientation) => void;
}

const PageOrientationSelect = ({ value = 'portrait', onChange }: PageOrientationSelectProps) => {
  const { t } = useTranslation();

  const options: OrientationOption[] = useMemo(
    () => [
      { value: 'portrait', label: t('document.page.orientation.portrait') },
      { value: 'landscape', label: t('document.page.orientation.landscape') },
    ],
    [t]
  );

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    const newValue = event.target.value as PageOrientation;
    trackDatadogAction({ action: 'Page Orientation Changed', data: { orientation: newValue } });
    onChange?.(newValue);
  };

  return (
    <Box sx={{ width: '100%' }} data-testid={pageOrientationSelectTestIds.container}>
      <TextField
        select
        fullWidth
        size="small"
        label={t('document.page.orientation.label')}
        value={value}
        onChange={handleChange}
        data-testid={pageOrientationSelectTestIds.select}
        inputProps={{ 'data-dd-action-name': 'Page Orientation Select' }}
        SelectProps={{
          MenuProps: {
            disableScrollLock: true,
          },
        }}
        sx={{
          '& .MuiInputBase-input': { fontSize: '16px' },
        }}
      >
        {options.map((option) => (
          <MenuItem key={option.value} value={option.value} data-dd-action-name={`Page Orientation Option ${option.value}`}>
            {option.label}
          </MenuItem>
        ))}
      </TextField>
    </Box>
  );
};

export default PageOrientationSelect;
